import React from 'react';
// @ts-ignore
import { Helmet } from 'react-helmet';

interface SEOMetadataProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  schemaMarkup?: object;
  noindex?: boolean;
  nofollow?: boolean;
  robots?: string;
  author?: string;
  language?: string;
  alternateLanguages?: Array<{
    href: string;
    hreflang: string;
  }>;
}

const SEOMetadataComponent: React.FC<SEOMetadataProps> = ({
  title,
  description,
  canonicalUrl,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  schemaMarkup,
  noindex = false,
  nofollow = false,
  robots,
  author,
  language = 'en',
  alternateLanguages = []
}) => {
  // Build robots meta content
  const buildRobotsContent = (): string => {
    if (robots) return robots;
    
    const robotsArray: string[] = [];
    if (noindex) robotsArray.push('noindex');
    if (nofollow) robotsArray.push('nofollow');
    
    return robotsArray.length > 0 ? robotsArray.join(', ') : 'index, follow';
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      
      {/* Language */}
      <html lang={language} />
      
      {/* Robots */}
      <meta name="robots" content={buildRobotsContent()} />
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Alternate Languages */}
      {alternateLanguages.map((alt, index) => (
        <link
          key={index}
          rel="alternate"
          hrefLang={alt.hreflang}
          href={alt.href}
        />
      ))}
      
      {/* Open Graph Meta Tags */}
      {(ogTitle || title) && (
        <meta property="og:title" content={ogTitle || title} />
      )}
      {(ogDescription || description) && (
        <meta property="og:description" content={ogDescription || description} />
      )}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {(ogUrl || canonicalUrl) && (
        <meta property="og:url" content={ogUrl || canonicalUrl} />
      )}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Invozio" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCard} />
      {(twitterTitle || ogTitle || title) && (
        <meta name="twitter:title" content={twitterTitle || ogTitle || title} />
      )}
      {(twitterDescription || ogDescription || description) && (
        <meta
          name="twitter:description"
          content={twitterDescription || ogDescription || description}
        />
      )}
      {(twitterImage || ogImage) && (
        <meta name="twitter:image" content={twitterImage || ogImage} />
      )}
      
      {/* Schema Markup */}
      {schemaMarkup && (
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOMetadataComponent;
 